import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField } from '@vibesites/runtime/react';
import { EditableToken } from '@vibesites/runtime/react';
import { FixedWaveBand } from './_fixedWaveTexture';

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

const styles = `
  .cmap-section {
    width: 100%;
  }
  .cmap-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .cmap-iframe {
    display: block;
    width: 100%;
    height: 480px;
    border: none;
  }
  @media (max-width: 767px) {
    .cmap-iframe { height: 320px; }
  }
  .cmap-placeholder {
    width: 100%;
    height: 480px;
    background: var(--vibe-color-surface, #f5f5f5);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: 767px) {
    .cmap-placeholder { height: 320px; }
  }
`;

export function ContactMap() {
  const heading = useContentToken('contactmap.heading');

  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords =
    lat && lng && lat.trim() !== '' && lng.trim() !== '';

  let src = '';
  if (hasCoords && MAPS_API_KEY) {
    src = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=15`;
  } else if (hasCoords) {
    src = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  } else if (address && address.trim() !== '') {
    src = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  return (
    <section className="cmap-section" aria-label={heading as string | undefined}>
      {/* Visually hidden heading for screen readers and editor discoverability */}
      <EditableToken path="contactmap.heading" as="h2" className="cmap-sr-only">
        {heading}
      </EditableToken>

      {src ? (
        <iframe
          className="cmap-iframe"
          src={src}
          title={heading as string | undefined}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="cmap-placeholder">
          <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
            Map location not available
          </p>
        </div>
      )}

      {/* Decorative wave band closing out the page above the footer — the
          site-wide wave motif. */}
      <FixedWaveBand className="w-full h-[35px] lg:h-[70px]" />

      <style>{styles}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactMap',
  displayName: 'Contact Map',
  tokens: {
    'contactmap.heading': {
      label: 'Section Heading (screen-reader label)',
      type: 'text',
      defaultValue: 'Find Us',
    },
  },
};

(ContactMap as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactMap;
